import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Trash2, ShieldAlert, Database, Loader2, RotateCcw, Archive, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

const CONFIRM_PHRASE = 'CANCELLA-ORDINI-E-LOTTI';
const RESTORE_CONFIRM_PHRASE = 'RIPRISTINA-BACKUP';

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

export default function EnvironmentResetPage() {
  const { user } = useAuth();
  const [typed, setTyped] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const isAdmin = user?.role === 'admin';
  const phraseOk = typed === CONFIRM_PHRASE;

  const loadBackups = useCallback(async () => {
    setBackupsLoading(true);
    try {
      const res = await api.get('/admin/maintenance/backups');
      setBackups(res.data || []);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Errore caricamento backup';
      toast.error(String(msg));
    } finally {
      setBackupsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) loadBackups();
  }, [isAdmin, loadBackups]);

  const handleReset = async () => {
    setLoading(true);
    try {
      const res = await api.post('/admin/maintenance/reset-orders-lots', {
        confirm: CONFIRM_PHRASE,
      });
      setLastResult(res.data);
      toast.success('Reset completato. Backup salvato.');
      setTyped('');
      setOpenConfirm(false);
      loadBackups();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Errore durante il reset';
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;
    setRestoreLoading(true);
    try {
      const res = await api.post(
        `/admin/maintenance/backups/${restoreTarget.timestamp}/restore`,
        { confirm: RESTORE_CONFIRM_PHRASE }
      );
      toast.success(`Backup ${restoreTarget.timestamp} ripristinato. Snapshot pre-restore salvato.`);
      setRestoreTarget(null);
      setLastResult(null);
      loadBackups();
      // Show summary card
      setLastResult({
        backup_dir: res.data.pre_restore_backup_dir,
        deleted: {},
        remaining: res.data.inserted,
        executed_at: res.data.executed_at,
        executed_by: res.data.executed_by,
        _mode: 'restore',
        _restored_from: res.data.restored_from,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Errore durante il ripristino';
      toast.error(String(msg));
    } finally {
      setRestoreLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <>
        <div className="max-w-2xl mx-auto mt-10">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <ShieldAlert className="w-5 h-5" /> Accesso negato
              </CardTitle>
              <CardDescription>Questa sezione è riservata agli amministratori.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="environment-reset-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Reset Ambiente</h1>
            <p className="text-sm text-slate-500">Cancella ordini, righe ordine e lotti di produzione</p>
          </div>
        </div>

        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Operazione irreversibile
            </CardTitle>
            <CardDescription>
              Verranno cancellate tutte le voci dalle seguenti collezioni:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="text-sm text-slate-700 list-disc pl-5 space-y-1">
              <li><b>orders</b> — tutti gli ordini cliente</li>
              <li><b>order_lines</b> — tutte le righe d&apos;ordine</li>
              <li><b>production_lots</b> — tutti i lotti di produzione</li>
            </ul>
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-800">
              <b>Anagrafiche preservate:</b> clienti, prodotti, fornitori, macchine, dipendenti, utenti — NON verranno toccate.
            </div>
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <b>Backup automatico:</b> un dump JSON delle 3 collezioni viene salvato in <code>/app/backups/&lt;timestamp&gt;/</code> prima della cancellazione.
            </div>
            <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
              <b>Attenzione:</b> spedizioni, controlli qualità e non conformità collegati potrebbero rimanere orfani (riferimenti a ordini/lotti non più esistenti).
            </div>

            <div className="pt-2">
              <Label htmlFor="confirm-phrase" className="text-sm font-medium">
                Per confermare, digita la frase esatta: <code className="text-red-600">{CONFIRM_PHRASE}</code>
              </Label>
              <Input
                id="confirm-phrase"
                data-testid="reset-confirm-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="mt-2 font-mono"
                autoComplete="off"
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button
                variant="destructive"
                disabled={!phraseOk || loading}
                onClick={() => setOpenConfirm(true)}
                data-testid="reset-open-confirm-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cancella ordini e lotti
              </Button>
            </div>
          </CardContent>
        </Card>

        {lastResult && (
          <Card data-testid="reset-result-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-slate-800">
                <Database className="w-5 h-5" />
                {lastResult._mode === 'restore' ? 'Ultimo ripristino eseguito' : 'Ultimo reset eseguito'}
              </CardTitle>
              <CardDescription>
                Eseguito da <b>{lastResult.executed_by}</b> il {new Date(lastResult.executed_at).toLocaleString('it-IT')}
                {lastResult._mode === 'restore' && (
                  <> — Ripristinato dal backup <code>{lastResult._restored_from}</code></>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(lastResult._mode === 'restore' ? lastResult.remaining : lastResult.deleted).map(([k, v]) => (
                  <div key={k} className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500 uppercase">{k}</div>
                    <div className="text-2xl font-semibold text-slate-900">{v}</div>
                    <div className="text-xs text-slate-500">
                      {lastResult._mode === 'restore' ? 'righe ripristinate' : 'righe cancellate'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-xs text-slate-500">
                Backup: <code className="text-slate-700">{lastResult.backup_dir}</code>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Backup precedenti */}
        <Card data-testid="backups-list-card">
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2 text-slate-800">
                <Archive className="w-5 h-5" /> Backup precedenti
              </CardTitle>
              <CardDescription>
                Puoi ripristinare un backup in caso di ripensamento. Prima del ripristino viene comunque salvato uno snapshot dello stato attuale.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadBackups}
              disabled={backupsLoading}
              data-testid="backups-refresh-btn"
            >
              {backupsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
          </CardHeader>
          <CardContent>
            {backups.length === 0 && !backupsLoading && (
              <div className="text-sm text-slate-500 py-4 text-center">Nessun backup disponibile.</div>
            )}
            {backups.length > 0 && (
              <div className="divide-y divide-slate-200 border rounded-md">
                {backups.map((b) => (
                  <div
                    key={b.timestamp}
                    className="flex items-center justify-between px-4 py-3 hover:bg-slate-50"
                    data-testid={`backup-row-${b.timestamp}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 font-mono">{b.timestamp}</div>
                      <div className="text-xs text-slate-500">
                        {b.created_at ? new Date(b.created_at).toLocaleString('it-IT') : '—'} · {formatBytes(b.size_bytes)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge variant="secondary" className="text-xs">O: {b.counts.orders}</Badge>
                      <Badge variant="secondary" className="text-xs">R: {b.counts.order_lines}</Badge>
                      <Badge variant="secondary" className="text-xs">L: {b.counts.production_lots}</Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setRestoreTarget(b)}
                        disabled={b.total_docs === 0}
                        title={b.total_docs === 0 ? 'Backup vuoto' : 'Ripristina questo backup'}
                        data-testid={`backup-restore-btn-${b.timestamp}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                        Ripristina
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-3 text-xs text-slate-500">
              <b>O</b>: orders · <b>R</b>: order_lines · <b>L</b>: production_lots
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={openConfirm} onOpenChange={setOpenConfirm}>
        <AlertDialogContent data-testid="reset-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Confermi la cancellazione?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stai per cancellare <b>tutti</b> gli ordini, le righe d&apos;ordine e i lotti di produzione da questo ambiente.
              L&apos;operazione è irreversibile (verrà comunque salvato un backup JSON).
              Vuoi procedere?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading} data-testid="reset-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleReset(); }}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
              data-testid="reset-confirm-btn"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cancellazione…</>) : 'Sì, cancella tutto'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!restoreTarget} onOpenChange={(o) => !o && setRestoreTarget(null)}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <RotateCcw className="w-5 h-5" /> Confermi il ripristino?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Stai per ripristinare il backup <code className="font-mono">{restoreTarget?.timestamp}</code>.
              Il contenuto attuale di <b>orders</b>, <b>order_lines</b> e <b>production_lots</b> verrà
              sostituito con {restoreTarget?.counts?.orders ?? 0} ordini,{' '}
              {restoreTarget?.counts?.order_lines ?? 0} righe e{' '}
              {restoreTarget?.counts?.production_lots ?? 0} lotti.
              <br /><br />
              Prima del ripristino viene salvato uno snapshot dello stato attuale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreLoading} data-testid="restore-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRestore(); }}
              disabled={restoreLoading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="restore-confirm-btn"
            >
              {restoreLoading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ripristino…</>) : 'Sì, ripristina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
